import React, {useEffect, useState} from 'react';
import {Amplify, Storage} from 'aws-amplify'
import {useTranslation} from "react-i18next";
import aws_config from '../../modified_aws-export';
import {useNavigate} from 'react-router-dom';
import {IManualFinderFilterModel, IManualFinderModel} from "../../types/manualFinder";
import {MultiValue, SingleValue} from "react-select";
import {IOptionType} from "../../types/common";
import "./ManualFinder.scss";
import {ExtractedItem} from "../../utils/manualFinderUtils";
import {useExtractedImagesState, useFileUpload, useManualFinderDefaultValues} from "../../hooks";
import {
    ManualFinderAutomaticSelection,
    ManualFinderFilter,
    ManualFinderManualSelection,
    ManualFinderSpecialTools,
    NavigationTabs,
    PDFUploadCard,
    PDFUploadedCard
} from '../../features';
import {ConfirmationModal, PageActionBar, PageBody, PageHeader, PageLayout} from '../../components';
import {useLoadOptions} from "../../hooks/useLoadOptions";

Amplify.configure(aws_config);

const ManualFinderPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    Storage.configure({
        aws_user_files_s3_bucket_region: aws_config.aws_user_files_s3_bucket_region,
        aws_user_files_s3_bucket: aws_config.aws_user_files_s3_bucket
    })

    const {
        progress,
        isUploading,
        isUploadCompleted,
        handleUploadFiles,
        handleClearUploadedFile,
        uploadedFiles,
        fileMap,
        isModalOpen,
        confirmNavigation,
        cancelNavigation,
        setIsModalOpen
    } = useFileUpload();

    const {extractedTypeDefault, manualFinderFilterDefault, manualFinderPropsDefault,} = useManualFinderDefaultValues();
    const {extractedTypeOptions} = useLoadOptions();
    const { resetSelectedImages } = useExtractedImagesState();
    const [selectedExtractedType, setSelectedExtractedType] = useState<IOptionType>(extractedTypeDefault)
    const [isLoading, setIsLoading] = useState(false);
    const [extractionFinished, setExtractionFinished] = useState(false);
    const [manualFinderProps, setManualFinderProps] = useState<IManualFinderModel>(manualFinderPropsDefault)
    const [manualFinderFilterProps, setManualFinderFilterProps] = useState<IManualFinderFilterModel>(manualFinderFilterDefault)

    useEffect(() => {
        setExtractionFinished(false);
        resetSelectedImages();
    }, [resetSelectedImages, setExtractionFinished]);

    const handleValueChange = (value: number | SingleValue<IOptionType> | MultiValue<IOptionType> | boolean[], key: string) => {
        setManualFinderFilterProps(prevProps => ({
            ...prevProps,
            [key]: value
        }));
    };

    const handleConfirmNavigation = () => {
        confirmNavigation();
        handleClosePDFUploaded();
    }

    const handleClosePDFUploaded = () => {
        handleClearUploadedFile();
        setExtractionFinished(false);
        setSelectedExtractedType(extractedTypeDefault);
        setManualFinderFilterProps(manualFinderFilterDefault);
        setManualFinderProps(manualFinderPropsDefault);
        resetSelectedImages();
        navigate('/special-tools-finder');
    };

    const isDisplayingResult = () => {
        return extractionFinished && (manualFinderProps.specialTools.length > 0 || manualFinderProps.extractedImages.length > 0);
    }

    const renderAfterPDFUploaded = () => {
        if (isDisplayingResult()) {
            switch (selectedExtractedType.value) {
                case ExtractedItem.ImagesManual:
                    return <ManualFinderManualSelection extractedImages={manualFinderProps.extractedImages}/>;
                case ExtractedItem.ImagesAutomatic:
                    return <ManualFinderAutomaticSelection manualsBasedOnImages={manualFinderProps.manualsBasedOnImages}
                        setIsLoading={setIsLoading} />
                case ExtractedItem.SpecialTools:
                    return <ManualFinderSpecialTools manuals={manualFinderProps.manuals}
                        setIsLoading={setIsLoading} />
                default:
                    return <></>
            }
        }
        return (
            <div className="d-flex justify-content-center align-items-center pdf-upload-card-container">
                <PDFUploadedCard onClearFileUploaded={() => setIsModalOpen(true)}
                    manualFinderFilterProps={manualFinderFilterProps}
                    fileMap={fileMap}
                    setManualFinderProps={setManualFinderProps}
                    setExtractionFinished={setExtractionFinished}
                    onValueChange={handleValueChange}
                    uploadedFiles={uploadedFiles}
                    selectedExtractedType={selectedExtractedType}
                    setSelectedExtractedType={value => setSelectedExtractedType(value ?? extractedTypeDefault)}
                />
            </div>
        )
    }

    const renderManualFinderPageBody = () => {
        if (isUploadCompleted) {
            return <>
                <ConfirmationModal
                    show={isModalOpen}
                    title={t("ConfirmationModal.ConfirmationTitle")}
                    body={t("ConfirmationModal.ConfirmationMessage")}
                    onConfirm={handleConfirmNavigation}
                    onCancel={cancelNavigation}
                />
                {renderAfterPDFUploaded()}
            </>
        }
        return (
            <div className="d-flex justify-content-center align-items-center pdf-upload-card-container">
                <PDFUploadCard
                    isUploadCompleted={isUploadCompleted}
                    isUploading={isUploading}
                    progress={progress}
                    onUploadFiles={(files) => handleUploadFiles(files)}/>
            </div>
        )
    }

    return (
        <PageLayout>
            <PageHeader title={t("Manual.ManualFinder")}
                        isDisplayBack={isUploadCompleted}
                        onBackClick={() => setIsModalOpen(true)} t={t}/>
            {isDisplayingResult() && (
                <PageActionBar rightBar={<NavigationTabs
                    extractedTypeOptions={extractedTypeOptions}
                    selectedExtractedType={selectedExtractedType}
                    setSelectedExtractedType={setSelectedExtractedType}
                />}
                    leftBar={<ManualFinderFilter
                        selectedExtractedType={selectedExtractedType}
                        manualFinderFilterProps={manualFinderFilterProps}
                        handleValueChange={handleValueChange}
                        isLoading={isLoading}
                        t={t}
                        specialTools={manualFinderProps.specialTools}
                        setManualFinderProps={setManualFinderProps}
                        manualFinderProps={manualFinderProps}
                        setIsLoading={setIsLoading}
                    />}
                />)}
            <PageBody isLoading={isLoading}
                t={t}>
                {renderManualFinderPageBody()}
            </PageBody>
        </PageLayout>
    )
};


export default ManualFinderPage;
